/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    AlertTriangle, Ban, Box, ClipboardList, ExternalLink,
    FlaskConical, KeyRound, Mail, Package, RefreshCw, Search, ShieldCheck, ShoppingBag,
    Unlock, UserCog, UserRound, Users,
} from "lucide-react";
import CommerceItemImage from "../../../../components/store/CommerceItemImage.jsx";
import { SelectDropdown } from "../../../../components/ui/index.js";
import { confirmDelete, showAlert } from "../../../../components/HelperAlerts/HelpersAlerts.jsx";
import { getThemeContext } from "../../../../components/providers/ThemeProvider.jsx";

const TABS = [
    ["summary", "Resumen", UserRound],
    ["designs", "Modelos", Box],
    ["requests", "Solicitudes", ClipboardList],
    ["orders", "Pedidos", ShoppingBag],
    ["tokens", "Tokens", KeyRound],
];

const fmtCurrency = (value, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency }).format(Number(value || 0));

const formatDate = (value) => value ? new Date(value).toLocaleDateString("es-ES") : "Sin fecha";

const apiError = (error, fallback) => {
    const data = error.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    return Object.values(data).flat().join(" ") || fallback;
};

function StatusBadge({ active }) {
    return (
        <span className={`badge badge-sm ${active ? "badge-success" : "badge-error"}`}>
            {active ? "Activa" : "Bloqueada"}
        </span>
    );
}

export default function AdminUsers() {
    const navigate = useNavigate();
    const theme = getThemeContext();
    const [users, setUsers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [nextPage, setNextPage] = useState(false);
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("");
    const [verifiedFilter, setVerifiedFilter] = useState("");
    const [createdFrom, setCreatedFrom] = useState("");
    const [createdTo, setCreatedTo] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [tokens, setTokens] = useState(null);
    const [tab, setTab] = useState("summary");
    const [loading, setLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState("");
    const [testerReason, setTesterReason] = useState("");
    const [testerSaving, setTesterSaving] = useState(false);
    const [roleDraft, setRoleDraft] = useState("");
    const [roleReason, setRoleReason] = useState("");
    const [roleSaving, setRoleSaving] = useState(false);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get("/users-admin/", {
                params: {
                    page,
                    search: appliedSearch || undefined,
                    is_active: activeFilter || undefined,
                    email_verified: verifiedFilter || undefined,
                    created_from: createdFrom || undefined,
                    created_to: createdTo || undefined,
                },
            });
            const results = data.results ?? data;
            setUsers(results);
            setTotalCount(data.count ?? results.length);
            setNextPage(Boolean(data.next));
            setSelectedId((current) => (
                results.some((user) => user.id === current) ? current : (results[0]?.id || null)
            ));
        } catch (err) {
            setError(apiError(err, "No se pudieron cargar los usuarios."));
        } finally {
            setLoading(false);
        }
    }, [page, appliedSearch, activeFilter, verifiedFilter, createdFrom, createdTo]);

    const fetchDetail = useCallback(async (userId) => {
        if (!userId) return;
        setDetailLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`/users-admin/${userId}/`);
            setDetail(data);
            setTokens(null);
        } catch (err) {
            setError(apiError(err, "No se pudo cargar la ficha del usuario."));
        } finally {
            setDetailLoading(false);
        }
    }, []);

    const fetchTokens = useCallback(async () => {
        if (!selectedId) return;
        try {
            const { data } = await axios.get(`/users-admin/${selectedId}/tokens/`);
            setTokens(data);
        } catch (err) {
            setError(apiError(err, "No se pudieron cargar los tokens."));
        }
    }, [selectedId]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);
    useEffect(() => {
        if (selectedId) {
            fetchDetail(selectedId);
        } else {
            setDetail(null);
            setTokens(null);
        }
    }, [selectedId, fetchDetail]);
    useEffect(() => {
        if (tab === "tokens" && !tokens) fetchTokens();
    }, [tab, tokens, fetchTokens]);

    const applySearch = () => {
        setPage(1);
        setAppliedSearch(search.trim());
    };

    const toggleUser = async () => {
        const user = detail?.user;
        if (!user?.can_manage_status) return;
        const nextActive = !user.is_active;
        const confirmed = await confirmDelete({
            title: nextActive ? "¿Reactivar cuenta?" : "¿Bloquear cuenta?",
            text: nextActive
                ? `${user.email} podrá volver a iniciar sesión.`
                : `${user.email} perderá el acceso, pero sus datos se conservarán.`,
            confirmButtonText: nextActive ? "Sí, reactivar" : "Sí, bloquear",
            cancelButtonText: "Cancelar",
            theme,
        });
        if (!confirmed) return;
        try {
            await axios.patch(`/users-admin/${user.id}/`, { is_active: nextActive });
            setDetail((current) => current ? {
                ...current,
                user: { ...current.user, is_active: nextActive },
            } : current);
            await fetchUsers();
            showAlert({
                title: nextActive ? "Cuenta reactivada" : "Cuenta bloqueada",
                icon: "success",
                theme,
            });
        } catch (err) {
            setError(apiError(err, "No se pudo actualizar la cuenta."));
        }
    };

    const toggleTester = async () => {
        const user = detail?.user;
        const reason = testerReason.trim();
        if (!user || testerSaving) return;
        if (!reason) {
            setError("Indica un motivo para conceder o revocar la condición tester.");
            return;
        }
        setTesterSaving(true);
        setError("");
        try {
            await axios.patch(`/users-admin/${user.id}/`, {
                is_tester: !user.is_tester,
                tester_reason: reason,
            });
            setTesterReason("");
            await fetchDetail(user.id);
            await fetchUsers();
        } catch (err) {
            setError(apiError(err, "No se pudo actualizar la condición tester."));
        } finally {
            setTesterSaving(false);
        }
    };

    // El selector arranca en el rol actual cada vez que se abre una ficha.
    useEffect(() => {
        setRoleDraft(detail?.user?.role ?? "");
        setRoleReason("");
    }, [detail?.user?.id, detail?.user?.role]);

    const changeRole = async () => {
        const user = detail?.user;
        const reason = roleReason.trim();
        if (!user || roleSaving || roleDraft === user.role) return;
        if (!reason) {
            setError("Indica un motivo para cambiar el rol.");
            return;
        }
        setRoleSaving(true);
        setError("");
        try {
            await axios.patch(`/users-admin/${user.id}/`, { role: roleDraft, role_reason: reason });
            await fetchDetail(user.id);
            await fetchUsers();
        } catch (err) {
            setError(apiError(err, "No se pudo cambiar el rol."));
        } finally {
            setRoleSaving(false);
        }
    };

    const revokePublicToken = async (token) => {
        const confirmed = await confirmDelete({
            title: "¿Revocar enlace público?",
            text: `El enlace del modelo “${token.model.name}” dejará de funcionar.`,
            confirmButtonText: "Revocar",
            theme,
        });
        if (!confirmed) return;
        try {
            await axios.delete(`/public-tokens/${token.id}/`);
            await fetchTokens();
        } catch (err) {
            setError(apiError(err, "No se pudo revocar el enlace público."));
        }
    };

    const revokePurchaseToken = async (token) => {
        const confirmed = await confirmDelete({
            title: "¿Revocar enlace de presupuesto?",
            text: `La solicitud #${token.request_id} dejará de poder comprarse desde ese enlace.`,
            confirmButtonText: "Revocar",
            theme,
        });
        if (!confirmed) return;
        try {
            await axios.post(`/custom-order-requests-admin/${token.request_id}/purchase-token/revoke/`);
            await fetchDetail(selectedId);
            await fetchTokens();
        } catch (err) {
            setError(apiError(err, "No se pudo revocar el enlace de presupuesto."));
        }
    };

    const renderTab = () => {
        if (!detail) return null;
        if (tab === "summary") {
            return (
                <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                    {[
                        ["Modelos", detail.stats.designs, Box],
                        ["Solicitudes", detail.stats.requests, ClipboardList],
                        ["Pedidos", detail.stats.orders, ShoppingBag],
                        ["Facturación", fmtCurrency(detail.stats.total_spent), Package],
                    ].map(([label, value, Icon]) => (
                        <div key={label} className="rounded-xl bg-base-200/70 border border-base-300 p-4">
                            <Icon className="w-5 h-5 text-primary mb-2" />
                            <p className="text-2xl font-bold">{value}</p>
                            <p className="text-xs text-base-content/60">{label}</p>
                        </div>
                    ))}
                    <div className="col-span-2 xl:col-span-4 rounded-xl border border-primary/30 bg-primary/5 p-4">
                        <div className="flex items-start gap-3">
                            <FlaskConical className="w-5 h-5 text-primary mt-0.5" />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-semibold">Tester: productos digitales gratis</p>
                                    <span className={`badge badge-sm ${detail.user.is_tester ? "badge-success" : "badge-ghost"}`}>
                                        {detail.user.is_tester ? "Activa" : "Inactiva"}
                                    </span>
                                </div>
                                <p className="text-xs text-base-content/60 mt-1">Solo habilita productos digitales a coste cero; no modifica precios físicos.</p>
                                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                                    <input
                                        className="input input-bordered input-sm flex-1"
                                        value={testerReason}
                                        onChange={(event) => setTesterReason(event.target.value)}
                                        placeholder="Motivo de la concesión o revocación"
                                        aria-label="Motivo de cambio de tester"
                                    />
                                    <button
                                        className={`btn btn-sm ${detail.user.is_tester ? "btn-warning" : "btn-primary"}`}
                                        onClick={toggleTester}
                                        disabled={testerSaving}
                                    >
                                        {testerSaving && <span className="loading loading-spinner loading-xs" />}
                                        {detail.user.is_tester ? "Revocar tester" : "Conceder tester"}
                                    </button>
                                </div>
                                {detail.tester_status_events?.length > 0 && (
                                    <div className="mt-3 space-y-1 text-xs text-base-content/60">
                                        {detail.tester_status_events.slice(0, 3).map((event) => (
                                            <p key={event.id}>
                                                {event.action_display} · {event.actor_email} · {formatDate(event.created_at)}
                                                {event.reason ? ` · ${event.reason}` : ""}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-span-2 xl:col-span-4 rounded-xl border border-base-300 p-4">
                        <div className="flex items-start gap-3">
                            <UserCog className="w-5 h-5 text-primary mt-0.5" />
                            <div className="min-w-0 flex-1">
                                <p className="font-semibold">Rol</p>
                                <p className="text-xs text-base-content/60 mt-1">
                                    Lo que puede hacer cada rol se decide en Roles y permisos. El rol admin
                                    solo lo da o lo quita un superusuario.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                                    <select
                                        className="select select-bordered select-sm"
                                        value={roleDraft}
                                        onChange={(event) => setRoleDraft(event.target.value)}
                                        disabled={roleSaving || detail.user.is_superuser
                                            || (detail.user.role === "admin" && !detail.can_manage_admin_role)}
                                        aria-label="Rol del usuario"
                                    >
                                        {(detail.role_choices || []).map((choice) => (
                                            <option
                                                key={choice.value}
                                                value={choice.value}
                                                disabled={choice.value === "admin" && !detail.can_manage_admin_role}
                                            >
                                                {choice.label}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        className="input input-bordered input-sm flex-1"
                                        value={roleReason}
                                        onChange={(event) => setRoleReason(event.target.value)}
                                        placeholder="Motivo del cambio"
                                        aria-label="Motivo del cambio de rol"
                                        disabled={roleDraft === detail.user.role}
                                    />
                                    <button
                                        className="btn btn-sm btn-primary"
                                        onClick={changeRole}
                                        disabled={roleSaving || roleDraft === detail.user.role}
                                    >
                                        {roleSaving && <span className="loading loading-spinner loading-xs" />}
                                        Cambiar rol
                                    </button>
                                </div>
                                {detail.role_change_events?.length > 0 && (
                                    <div className="mt-3 space-y-1 text-xs text-base-content/60">
                                        {detail.role_change_events.slice(0, 3).map((event) => (
                                            <p key={event.id}>
                                                {event.from_role} → {event.to_role} · {event.actor_email} · {formatDate(event.created_at)}
                                                {event.reason ? ` · ${event.reason}` : ""}
                                            </p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-span-2 xl:col-span-4 rounded-xl border border-base-300 p-4 grid sm:grid-cols-2 gap-3 text-sm">
                        <div><span className="text-base-content/50 block text-xs">Email</span>{detail.user.email}</div>
                        <div><span className="text-base-content/50 block text-xs">Teléfono</span>{detail.user.phone || "No indicado"}</div>
                        <div><span className="text-base-content/50 block text-xs">Rol</span>{detail.user.role}</div>
                        <div><span className="text-base-content/50 block text-xs">Alta</span>{formatDate(detail.user.created_at)}</div>
                    </div>
                </div>
            );
        }
        if (tab === "designs") {
            return detail.designs.length ? (
                <div className="grid sm:grid-cols-2 gap-3">
                    {detail.designs.map((design) => (
                        <button key={design.id} onClick={() => navigate(`/viewer/${design.id}`)} className="flex items-center gap-3 rounded-xl border border-base-300 p-3 text-left hover:bg-base-200">
                            <CommerceItemImage customKeyring={design} alt={design.name} className="w-14 h-14" show3dOnHover />
                            <div className="min-w-0"><p className="font-semibold truncate">{design.name}</p><p className="text-xs text-base-content/50">{formatDate(design.created_at)}</p></div>
                            <ExternalLink className="w-4 h-4 ml-auto text-primary" />
                        </button>
                    ))}
                </div>
            ) : <p className="text-base-content/50">Este usuario no tiene modelos.</p>;
        }
        if (tab === "requests") {
            return (
                <div className="space-y-2">
                    <button className="btn btn-sm btn-outline" onClick={() => navigate(`/admin/solicitudes?user_id=${selectedId}`)}>Abrir solicitudes filtradas</button>
                    {detail.requests.map((item) => (
                        <div key={item.id} className="rounded-xl border border-base-300 p-3 flex items-center gap-3">
                            <CommerceItemImage customKeyring={item.customized_keyring_detail} alt={item.customized_keyring_detail?.name} className="w-12 h-12" />
                            <div className="min-w-0 flex-1"><p className="font-semibold truncate">{item.customized_keyring_detail?.name}</p><p className="text-xs text-base-content/50">Solicitud #{item.id} · {formatDate(item.created_at)}</p></div>
                            <span className="badge badge-sm">{item.commercial_status}</span>
                        </div>
                    ))}
                    {!detail.requests.length && <p className="text-base-content/50">No hay solicitudes.</p>}
                </div>
            );
        }
        if (tab === "orders") {
            return (
                <div className="space-y-2">
                    <button className="btn btn-sm btn-outline" onClick={() => navigate(`/admin/pedidos?user_id=${selectedId}`)}>Abrir pedidos filtrados</button>
                    {detail.orders.map((order) => (
                        <div key={order.id} className="rounded-xl border border-base-300 p-3 flex items-center gap-3">
                            <div className="flex-1"><p className="font-mono font-semibold">{order.order_number}</p><p className="text-xs text-base-content/50">{formatDate(order.created_at)} · {order.status_display}</p></div>
                            <span className="font-semibold">{fmtCurrency(order.total_price, order.currency)}</span>
                        </div>
                    ))}
                    {!detail.orders.length && <p className="text-base-content/50">No hay pedidos.</p>}
                </div>
            );
        }
        return (
            <div className="space-y-5">
                <section><h4 className="font-semibold mb-2">Enlaces públicos</h4><div className="space-y-2">
                    {(tokens?.public_tokens || []).map((token) => (
                        <div key={token.id} className="rounded-xl border border-base-300 p-3 flex items-center gap-3">
                            <div className="flex-1 min-w-0"><p className="font-semibold truncate">{token.model.name}</p><p className="text-xs text-base-content/50">{token.token_masked} · {token.usage_count}/{token.max_usage || "∞"} usos · {token.status}</p></div>
                            {token.status === "active" && <button className="btn btn-xs btn-error btn-outline" onClick={() => revokePublicToken(token)}>Revocar</button>}
                        </div>
                    ))}
                    {!tokens?.public_tokens?.length && <p className="text-base-content/50 text-sm">No hay enlaces públicos.</p>}
                </div></section>
                <section><h4 className="font-semibold mb-2">Enlaces de presupuesto</h4><div className="space-y-2">
                    {(tokens?.purchase_tokens || []).map((token) => (
                        <div key={token.id} className="rounded-xl border border-base-300 p-3 flex items-center gap-3">
                            <div className="flex-1 min-w-0"><p className="font-semibold truncate">Solicitud #{token.request_id} · {token.model_name}</p><p className="text-xs text-base-content/50">{token.token_masked} · {token.status}</p></div>
                            {token.status === "active" && <button className="btn btn-xs btn-error btn-outline" onClick={() => revokePurchaseToken(token)}>Revocar</button>}
                        </div>
                    ))}
                    {!tokens?.purchase_tokens?.length && <p className="text-base-content/50 text-sm">No hay enlaces de presupuesto.</p>}
                </div></section>
            </div>
        );
    };

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            <div className="glassCard rounded-xl p-3 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3"><Users className="w-6 h-6 text-primary" /><div><h2 className="text-xl font-semibold">Usuarios</h2><p className="text-sm text-base-content/60">{totalCount} cuentas</p></div></div>
                    <button className="btn btn-sm btn-outline gap-2" onClick={fetchUsers}><RefreshCw className="w-4 h-4" />Actualizar</button>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <label className="input input-bordered input-sm flex items-center gap-2"><Search className="w-4 h-4 opacity-60" /><input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && applySearch()} placeholder="Email, nombre o usuario" /></label>
                    <button className="btn btn-sm btn-primary" onClick={applySearch}>Buscar</button>
                    <SelectDropdown size="sm" value={activeFilter} onChange={(value) => { setPage(1); setActiveFilter(value); }} options={[{ value: "", label: "Todas" }, { value: "true", label: "Activas" }, { value: "false", label: "Bloqueadas" }]} />
                    <SelectDropdown size="sm" value={verifiedFilter} onChange={(value) => { setPage(1); setVerifiedFilter(value); }} options={[{ value: "", label: "Cualquier verificación" }, { value: "true", label: "Verificadas" }, { value: "false", label: "Sin verificar" }]} />
                    <input type="date" className="input input-bordered input-sm" value={createdFrom} onChange={(event) => { setPage(1); setCreatedFrom(event.target.value); }} aria-label="Alta desde" />
                    <input type="date" className="input input-bordered input-sm" value={createdTo} onChange={(event) => { setPage(1); setCreatedTo(event.target.value); }} aria-label="Alta hasta" />
                </div>
            </div>
            {error && <div role="alert" className="alert alert-error"><AlertTriangle className="w-5 h-5" /><span>{error}</span></div>}
            <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-3 flex-1 min-h-0">
                <aside className="glassCard rounded-xl overflow-auto scrollbar_mod p-2">
                    {loading ? <div className="h-full grid place-items-center"><span className="loading loading-spinner loading-lg text-primary" /></div> : users.map((user) => (
                        <button key={user.id} onClick={() => { setSelectedId(user.id); setTab("summary"); }} className={`w-full text-left rounded-xl p-3 mb-1 border transition-colors ${selectedId === user.id ? "border-primary bg-primary/10" : "border-transparent hover:bg-base-200"}`}>
                            <div className="flex items-start gap-3"><div className="w-10 h-10 rounded-full bg-base-300 grid place-items-center font-semibold">{(user.name || user.email).slice(0, 2).toUpperCase()}</div><div className="min-w-0 flex-1"><p className="font-semibold truncate">{user.name || "Sin nombre"}</p><p className="text-xs text-base-content/60 truncate">{user.email}</p><div className="mt-2 flex gap-1 flex-wrap"><StatusBadge active={user.is_active} />{user.email_verified && <span className="badge badge-info badge-sm">Verificada</span>}{user.role && user.role !== "user" && <span className="badge badge-outline badge-sm">{user.role}</span>}{user.is_tester && <span className="badge badge-success badge-sm">Tester</span>}{user.is_superuser && <span className="badge badge-secondary badge-sm">Superusuario</span>}</div></div></div>
                        </button>
                    ))}
                    {!loading && !users.length && <p className="text-center text-base-content/50 py-10">No hay usuarios con estos filtros.</p>}
                    <div className="flex justify-between p-2"><button className="btn btn-xs" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Anterior</button><span className="text-xs self-center">Página {page}</span><button className="btn btn-xs" disabled={!nextPage} onClick={() => setPage((value) => value + 1)}>Siguiente</button></div>
                </aside>
                <section className="glassCard rounded-xl overflow-auto scrollbar_mod p-4">
                    {detailLoading ? <div className="h-full grid place-items-center"><span className="loading loading-spinner loading-lg text-primary" /></div> : detail ? <>
                        <div className="flex items-start justify-between gap-3 flex-wrap border-b border-base-300 pb-4"><div><div className="flex items-center gap-2"><h3 className="text-xl font-bold">{detail.user.name || detail.user.email}</h3><StatusBadge active={detail.user.is_active} /></div><p className="text-sm text-base-content/60 flex items-center gap-1"><Mail className="w-3 h-3" />{detail.user.email}</p><p className="text-xs text-base-content/50 mt-1">{detail.user.email_verified ? "Email verificado" : "Email sin verificar"}</p></div><button className={`btn btn-sm gap-2 ${detail.user.is_active ? "btn-error btn-outline" : "btn-success"}`} disabled={!detail.user.can_manage_status} onClick={toggleUser}>{detail.user.is_active ? <Ban className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}{detail.user.is_active ? "Bloquear" : "Reactivar"}</button></div>
                        <div role="tablist" className="tabs tabs-boxed my-4 flex-wrap">{TABS.map(([value, label, Icon]) => <button key={value} role="tab" className={`tab gap-1 ${tab === value ? "tab-active" : ""}`} onClick={() => setTab(value)}><Icon className="w-4 h-4" />{label}</button>)}</div>
                        {renderTab()}
                    </> : <div className="h-full grid place-items-center text-base-content/50"><ShieldCheck className="w-10 h-10" /><p>Selecciona un usuario.</p></div>}
                </section>
            </div>
        </div>
    );
}
